// Shared Brand Studio contract — used by the generation route (tool schema)
// AND the client brand-book renderer, so the fonts the model may pick are
// exactly the fonts we can load.

export interface BrandFont {
  name: string;                                  // family name, exactly as Google Fonts spells it
  category: 'sans' | 'serif' | 'display' | 'mono';
  /** Google Fonts css2 `family=` value incl. weights. */
  googleParam: string;
}

// Curated, web-safe-to-load set. Keep names in sync with FONT_NAMES below.
export const BRAND_FONTS: BrandFont[] = [
  { name: 'Inter',              category: 'sans',    googleParam: 'Inter:wght@400;500;600;700;800;900' },
  { name: 'Poppins',            category: 'sans',    googleParam: 'Poppins:wght@400;500;600;700;800' },
  { name: 'Montserrat',         category: 'sans',    googleParam: 'Montserrat:wght@400;500;600;700;800;900' },
  { name: 'Archivo',            category: 'sans',    googleParam: 'Archivo:wght@400;500;600;700;800;900' },
  { name: 'Work Sans',          category: 'sans',    googleParam: 'Work+Sans:wght@400;500;600;700;800' },
  { name: 'DM Sans',            category: 'sans',    googleParam: 'DM+Sans:wght@400;500;600;700' },
  { name: 'Manrope',            category: 'sans',    googleParam: 'Manrope:wght@400;500;600;700;800' },
  { name: 'Plus Jakarta Sans',  category: 'sans',    googleParam: 'Plus+Jakarta+Sans:wght@400;500;600;700;800' },
  { name: 'Figtree',            category: 'sans',    googleParam: 'Figtree:wght@400;500;600;700;800;900' },
  { name: 'Playfair Display',   category: 'serif',   googleParam: 'Playfair+Display:wght@400;500;600;700;800;900' },
  { name: 'Lora',               category: 'serif',   googleParam: 'Lora:wght@400;500;600;700' },
  { name: 'Merriweather',       category: 'serif',   googleParam: 'Merriweather:wght@400;700;900' },
  { name: 'Cormorant Garamond', category: 'serif',   googleParam: 'Cormorant+Garamond:wght@400;500;600;700' },
  { name: 'Libre Baskerville',  category: 'serif',   googleParam: 'Libre+Baskerville:wght@400;700' },
  { name: 'Fraunces',           category: 'serif',   googleParam: 'Fraunces:wght@400;500;600;700;900' },
  { name: 'Bricolage Grotesque',category: 'display', googleParam: 'Bricolage+Grotesque:wght@400;500;600;700;800' },
  { name: 'Fredoka',            category: 'display', googleParam: 'Fredoka:wght@400;500;600;700' },
  { name: 'Nunito',            category: 'display',  googleParam: 'Nunito:wght@400;600;700;800;900' },
  { name: 'Space Grotesk',      category: 'display', googleParam: 'Space+Grotesk:wght@400;500;600;700' },
];

export const FONT_NAMES = BRAND_FONTS.map((f) => f.name);

export function fontByName(name?: string): BrandFont | undefined {
  if (!name) return undefined;
  return BRAND_FONTS.find((f) => f.name.toLowerCase() === name.toLowerCase());
}

/** CSS font-family stack incl. sensible fallback for the family's category. */
export function fontStack(name?: string): string {
  const f = fontByName(name);
  if (!f) return "'Inter', system-ui, sans-serif";
  const fallback =
    f.category === 'serif' ? 'Georgia, serif'
    : f.category === 'mono' ? 'ui-monospace, monospace'
    : 'system-ui, sans-serif';
  return `'${f.name}', ${fallback}`;
}

/** Build a single Google Fonts css2 URL for the given families (deduped). */
export function googleFontsUrl(names: (string | undefined)[]): string {
  const params = Array.from(
    new Set(names.map((n) => fontByName(n)?.googleParam).filter(Boolean) as string[]),
  );
  if (params.length === 0) return '';
  return `https://fonts.googleapis.com/css2?${params.map((p) => `family=${p}`).join('&')}&display=swap`;
}

export const HEX = /^#[0-9a-fA-F]{6}$/;

/** Relative luminance → readable ink color (#ffffff or #111827) for text on `hex`. */
export function readableOn(hex?: string): string {
  if (!hex || !HEX.test(hex)) return '#111827';
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return L > 0.45 ? '#111827' : '#ffffff';
}

export const PALETTE_KEYS = [
  'primaryColor',
  'secondaryColor',
  'accentColor',
  'backgroundColorLight',
  'backgroundColorDark',
] as const;

// ── UI token → CSS mappings (ONE source of truth for book AND consumers) ──────

export const RADIUS_PX: Record<string, number> = { sharp: 0, slight: 6, rounded: 14, pill: 999 };
export const BORDER_PX: Record<string, number> = { none: 0, thin: 1, medium: 2, thick: 3 };
export const ELEVATION_SHADOW: Record<string, string> = {
  none: 'none',
  subtle: '0 1px 2px rgba(0,0,0,0.08)',
  medium: '0 4px 12px rgba(0,0,0,0.12)',
  strong: '0 10px 30px rgba(0,0,0,0.18)',
};

export const radiusPx = (v?: string): number => RADIUS_PX[v ?? 'rounded'] ?? 14;
export const borderPx = (v?: string): number => BORDER_PX[v ?? 'none'] ?? 0;
export const elevationShadow = (v?: string): string => ELEVATION_SHADOW[v ?? 'none'] ?? 'none';

/** Mix a hex toward white (amount>0) or black (amount<0), amount in -1..1. */
export function shade(hex: string, amount: number): string {
  if (!HEX.test(hex)) return hex;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const mix = (c: number) => (amount >= 0 ? c + (255 - c) * amount : c * (1 + amount));
  const h = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${h(mix(r))}${h(mix(g))}${h(mix(b))}`;
}

export function hexToHsl(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  const l = (max + min) / 2;
  let hue = 0, s = 0;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hue = (b - r) / d + 2; break;
      default: hue = (r - g) / d + 4;
    }
    hue /= 6;
  }
  return [hue * 360, s, l];
}

export function hslToHex(hDeg: number, s: number, l: number): string {
  const hh = ((((hDeg % 360) + 360) % 360) / 360);
  const f = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  let r: number, g: number, b: number;
  if (s === 0) { r = g = b = l; }
  else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = f(p, q, hh + 1 / 3); g = f(p, q, hh); b = f(p, q, hh - 1 / 3);
  }
  const to = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255))).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

/** 50..900 ramp in HSL: keeps the base HUE, walks canonical lightness targets and
 *  eases saturation down at the extremes → clean, branded steps (not the muddy
 *  desaturated mids a linear-RGB mix toward white/black produces). */
export function colorRamp(hex: string): Record<string, string> {
  if (!HEX.test(hex)) return { '500': hex };
  const [h, s] = hexToHsl(hex);
  const steps: [string, number, number][] = [
    ['50', 0.96, 0.55], ['100', 0.92, 0.65], ['200', 0.84, 0.78], ['300', 0.74, 0.88], ['400', 0.63, 0.96],
    ['500', 0.52, 1], ['600', 0.44, 1], ['700', 0.36, 0.98], ['800', 0.28, 0.92], ['900', 0.20, 0.85],
  ];
  const out: Record<string, string> = {};
  for (const [k, L, sMul] of steps) out[k] = hslToHex(h, Math.max(0, Math.min(1, s * sMul)), L);
  return out;
}

export interface ButtonRender { base: Record<string, any>; hover: Record<string, any> }

/** Resolve a button's default + hover inline styles from ui tokens + palette, so
 *  the Brand Book preview and the real site render pixel-identically. */
export function buttonRender(opts: {
  role: string; onRole: string; bg: string;
  style?: string; hover?: string; radius?: string; border?: string; elevation?: string;
}): ButtonRender {
  const radius = radiusPx(opts.radius);
  const shadow = elevationShadow(opts.elevation);
  const style = opts.style || 'fill';
  const bw = Math.max(borderPx(opts.border), style === 'outline' ? 2 : 0);
  let base: Record<string, any> = {
    borderRadius: radius, borderWidth: bw, borderStyle: bw ? 'solid' : 'none',
    boxShadow: shadow, transition: 'all .15s ease',
  };
  if (style === 'fill') base = { ...base, background: opts.role, color: opts.onRole, borderColor: opts.role };
  else if (style === 'outline') base = { ...base, background: 'transparent', color: opts.role, borderColor: opts.role };
  else if (style === 'ghost') base = { ...base, background: 'transparent', color: opts.role, borderColor: 'transparent' };
  else if (style === 'soft') base = { ...base, background: shade(opts.role, 0.82), color: shade(opts.role, -0.3), borderColor: 'transparent' };

  const hoverKind = opts.hover || 'darken';
  let hover: Record<string, any> = {};
  if (hoverKind === 'darken') hover = { background: style === 'fill' ? shade(opts.role, -0.15) : shade(opts.role, 0.9), color: style === 'fill' ? opts.onRole : opts.role };
  else if (hoverKind === 'lighten') hover = { background: style === 'fill' ? shade(opts.role, 0.15) : shade(opts.role, 0.9), color: style === 'fill' ? opts.onRole : opts.role };
  else if (hoverKind === 'lift') hover = { transform: 'translateY(-2px)', boxShadow: elevationShadow('strong'), background: style === 'fill' ? opts.role : shade(opts.role, 0.92), color: style === 'fill' ? opts.onRole : opts.role };
  else if (hoverKind === 'invert') hover = { background: style === 'fill' ? opts.bg : opts.role, color: style === 'fill' ? opts.role : opts.onRole };
  else if (hoverKind === 'glow') hover = { boxShadow: `0 0 0 4px ${shade(opts.role, 0.6)}`, background: style === 'fill' ? opts.role : 'transparent', color: style === 'fill' ? opts.onRole : opts.role };
  return { base, hover };
}
